import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { useStore } from '@/store';
import { formatPrice, getDomain } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function CheckoutForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { cart, storeInfo } = useStore();
  const location = useLocation();
  const { amount, email, name, paymentIntentId } = location.state || {};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      toast.error('Payment system not initialized');
      return;
    }

    if (!amount || !email) {
      toast.error('Invalid payment data');
      navigate('/cart');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create payment method
      const card = elements.getElement(CardElement);
      if (!card) {
        throw new Error('Card element not found');
      }

      // Create payment method and get token
      const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: {
          email,
          name: name || email
        }
      });

      if (methodError) {
        throw methodError;
      }

      // Complete payment on server
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}stripe-complete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          paymentIntentId,
          email,
          amount: Math.round(amount * 100),
          metadata: {
            orderId: `ord_${Math.floor(Math.random() * 1000000000)}`,
          }
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Payment failed');
      }

      if (result.status === 'succeeded') {
        toast.success('Payment successful!');
        
        localStorage.setItem('pendingStripeCart', JSON.stringify(cart));
        const formState = localStorage.getItem('stripeFormState');
        const parsedFormState = formState ? JSON.parse(formState) : {};
        
        navigate('/cart', {
          state: {
            orderId: paymentIntentId,
            paymentStatus: 'success',
            paymentMethod: 'stripe',
            ...parsedFormState
          },
        });
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      toast.error(err.message || 'Payment failed. Please check your card details and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="p-4 border rounded-lg bg-white">
        <CardElement options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }} />
      </div>
      {error && (
        <div className="text-red-500 text-sm mt-2">{error}</div>
      )}
      <Button
        type="submit"
        disabled={!stripe || isLoading}
        className="w-full"
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Processing...
          </div>
        ) : (
          `Pay ${formatPrice(amount, storeInfo?.currenCy)}`
        )}
      </Button>
    </form>
  );
}

export function StripePaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  
  const { paymentIntentId, amount, email } = location.state || {};

  useEffect(() => {
    if (!paymentIntentId || !amount || !email) {
      toast.error('Missing payment information');
      navigate('/cart');
      return;
    }

    // Validate amount
    if (amount <= 0) {
      toast.error('Invalid payment amount');
      navigate('/cart');
      return;
    }
  }, [paymentIntentId, amount, email, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Payment Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/cart')}
            className="w-full"
          >
            Return to Cart
          </Button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <Loader2 className="h-12 w-12 animate-spin text-brand-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Initializing Payment
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we set up your payment...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Complete Payment - Store</title>
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Complete Your Payment
          </h1>
          
          <Elements 
            stripe={stripePromise} 
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#0A2540',
                  colorBackground: '#ffffff',
                  colorText: '#30313d',
                  colorDanger: '#df1b41',
                  borderRadius: '4px',
                },
              },
            }}
          >
            <CheckoutForm />
          </Elements>
        </div>
      </div>
    </>
  );
}